export const WORKSPACE_BASE = {
  key: 'WORKSPACE',
  label: 'Private Workspace',
  short: 'Workspace',
  monthlyCents: 0,
  yearlyCents: 0,
  summary: 'Secure login, tenant isolation, contacts, inbox shell, team access, and billing.',
};

export const PLATFORM_FEATURES = {
  BULK_SMS: {
    key: 'BULK_SMS',
    label: 'Bulk SMS CRM',
    short: 'Bulk SMS',
    monthlyCents: 900,
    yearlyCents: 9000,
    lifetimeCents: 19900,
    summary: 'CSV import, tags, campaigns, opt-out list, delivery status, scheduled sends.',
  },
  WHATSAPP_API: {
    key: 'WHATSAPP_API',
    label: 'Direct WhatsApp API',
    short: 'WhatsApp API',
    monthlyCents: 900,
    yearlyCents: 9000,
    lifetimeCents: 19900,
    summary: 'Connected WhatsApp account, API send endpoint, reply forwarding, inbox sync.',
  },
  AI_AUTOMATION: {
    key: 'AI_AUTOMATION',
    label: 'AI + Automation Builder',
    short: 'Automation',
    monthlyCents: 900,
    yearlyCents: 9000,
    lifetimeCents: 14900,
    summary: 'Keyword replies, webhooks, AI assistant, IVR flows, digests, and external APIs.',
  },
  CALLS: {
    key: 'CALLS',
    label: 'Voice Calls',
    short: 'Calls',
    monthlyCents: 1500,
    yearlyCents: 15000,
    lifetimeCents: 24900,
    summary: 'Browser/mobile softphone, inbound/outbound calls, call logs, IVR, and fallback routing.',
  },
  EMAIL_MARKETING: {
    key: 'EMAIL_MARKETING',
    label: 'Email Marketing',
    short: 'Email',
    monthlyCents: 900,
    yearlyCents: 9000,
    lifetimeCents: 19900,
    summary: 'Verified sending domains, inbox email replies, bulk campaigns, opens, clicks, bounces, and suppressions.',
  },
};

export const DEFAULT_FEATURES = ['BULK_SMS'];

export const PRICING_TIERS = {
  STARTER: {
    key: 'STARTER',
    name: 'Starter',
    description: 'Bulk SMS workspace for simple campaigns, opt-ins, replies, tags, and delivery tracking.',
    monthlyCents: 1000,
    yearlyCents: 10000,
    features: ['BULK_SMS'],
    bullets: ['Bulk SMS campaigns', 'CSV imports and tags', 'STOP opt-out list', 'Delivery status'],
  },
  PRO: {
    key: 'PRO',
    name: 'Pro',
    description: 'SMS campaigns, Direct WhatsApp API, AI assistant, automation builder, short links, and webhooks.',
    monthlyCents: 4000,
    yearlyCents: 40000,
    features: ['BULK_SMS', 'WHATSAPP_API', 'AI_AUTOMATION'],
    bullets: ['Everything in Starter', 'Direct WhatsApp API', 'AI automation builder', 'Webhook intake and short links'],
  },
  ADVANCED: {
    key: 'ADVANCED',
    name: 'Advanced',
    description: 'Complete messaging suite with SMS, Direct WhatsApp API, AI automations, calls, IVR, and call routing.',
    monthlyCents: 12000,
    yearlyCents: 120000,
    features: ['BULK_SMS', 'WHATSAPP_API', 'AI_AUTOMATION', 'CALLS'],
    bullets: ['Everything in Pro', 'Browser and mobile calling', 'Inbound routing and IVR', 'Call logs and fallback handling'],
  },
};

export const FEATURE_BUNDLES = {
  whatsapp_api: ['WHATSAPP_API'],
  bulk_sms: ['BULK_SMS', 'AI_AUTOMATION'],
  calls: ['CALLS'],
  email: ['EMAIL_MARKETING'],
  complete: ['WHATSAPP_API', 'BULK_SMS', 'CALLS', 'AI_AUTOMATION', 'EMAIL_MARKETING'],
};

export function normalizeTier(tier = 'STARTER') {
  const key = String(tier || '').trim().toUpperCase();
  return PRICING_TIERS[key] ? key : 'STARTER';
}

export function normalizeBillingPeriod(period = 'month') {
  return String(period || '').toLowerCase() === 'year' ? 'year' : 'month';
}

export function priceKeyForBillingPeriod(period = 'month') {
  return normalizeBillingPeriod(period) === 'year' ? 'yearlyCents' : 'monthlyCents';
}

export function planFromTier({ tier = 'STARTER', billingPeriod = 'month' } = {}) {
  const tierKey = normalizeTier(tier);
  const period = normalizeBillingPeriod(billingPeriod);
  const plan = PRICING_TIERS[tierKey];
  const amountCents = period === 'year' ? plan.yearlyCents : plan.monthlyCents;

  return {
    tier: tierKey,
    billingPeriod: period,
    billingCycle: period === 'year' ? 'yearly' : 'monthly',
    features: plan.features,
    featureLabels: plan.features.map((feature) => PLATFORM_FEATURES[feature].label),
    totalCents: amountCents,
    monthlyEquivalentCents: period === 'year' ? Math.round(amountCents / 12) : amountCents,
    recurring: true,
    planName: `${plan.name} ${period === 'year' ? 'Annual' : 'Monthly'}`,
  };
}

export function normalizeFeatures(features = []) {
  return [...new Set(
    features.map((feature) => String(feature || '').trim().toUpperCase())
  )].filter((feature) => PLATFORM_FEATURES[feature]);
}

export function quoteFeaturePlan({ features = DEFAULT_FEATURES, billingPeriod = 'month' } = {}) {
  const selectedFeatures = normalizeFeatures(features);
  const period = normalizeBillingPeriod(billingPeriod);
  const priceKey = priceKeyForBillingPeriod(period);
  const lineItems = selectedFeatures.map((feature) => PLATFORM_FEATURES[feature]);
  const totalCents = lineItems.reduce((sum, item) => sum + Number(item[priceKey] || 0), 0);
  const featureLabels = selectedFeatures.map((feature) => PLATFORM_FEATURES[feature].label);
  const planLabel = featureLabels.length ? featureLabels.join(' + ') : 'Workspace';

  return {
    tier: 'CUSTOM',
    billingPeriod: period,
    billingCycle: period === 'year' ? 'yearly' : 'monthly',
    features: selectedFeatures,
    featureLabels,
    lineItems,
    totalCents,
    monthlyEquivalentCents: period === 'year' ? Math.round(totalCents / 12) : totalCents,
    recurring: true,
    planName: `${planLabel} ${period === 'year' ? 'Annual' : 'Monthly'}`,
  };
}

export function planFromSelection({ tier, features, billingPeriod = 'month' } = {}) {
  if (Array.isArray(features) || String(tier || '').toUpperCase() === 'CUSTOM') {
    return quoteFeaturePlan({ features: Array.isArray(features) ? features : DEFAULT_FEATURES, billingPeriod });
  }
  return planFromTier({ tier, billingPeriod });
}

export function quotePlan({ features = [], billingCycle = 'monthly' } = {}) {
  const selectedFeatures = normalizeFeatures(features);
  const cycle = billingCycle === 'lifetime' ? 'lifetime' : 'monthly';
  const priceKey = cycle === 'lifetime' ? 'lifetimeCents' : 'monthlyCents';
  const subtotalCents = selectedFeatures.reduce((sum, feature) => sum + PLATFORM_FEATURES[feature][priceKey], 0);
  const hasComplete = selectedFeatures.length >= Object.keys(PLATFORM_FEATURES).length;
  const discountCents = hasComplete && cycle === 'lifetime' ? Math.round(subtotalCents * 0.2) : 0;
  const totalCents = Math.max(0, subtotalCents - discountCents);

  return {
    billingCycle: cycle,
    features: selectedFeatures,
    featureLabels: selectedFeatures.map((feature) => PLATFORM_FEATURES[feature].label),
    subtotalCents,
    discountCents,
    totalCents,
    recurring: cycle === 'monthly',
    planName: selectedFeatures.length
      ? `${selectedFeatures.map((feature) => PLATFORM_FEATURES[feature].label).join(' + ')}${cycle === 'lifetime' ? ' Lifetime' : ' Monthly'}`
      : 'Custom Quote',
  };
}

export function money(cents) {
  return `$${(Number(cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}
